import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams, useParams } from 'react-router-dom';
// Icons removed from table headers for cleaner design
import { useTimeRange } from '../contexts/TimeRangeContext';
import { useEnvironment } from '../contexts/EnvironmentContext';
import { useApp } from '../contexts/AppContext';
import { useSessionList } from '../services/sessionsApi';
import { useNavigateWithRange } from '../hooks/useNavigateWithRange';
import ResizableTable from '../components/ResizableTable';
import { getTableRowStyle } from '../components/TablePrimitives';
import { layout, components, iconSize, iconShape, palette } from '../utils/designTokens';
import { formatRelativeTime, formatTokens, formatDuration, formatCost } from '../utils/formatters';
import { FilterPills, ListPageLayout, StatsBar, Stat, ErrorState, Breadcrumb } from '../components';
import useFilterManager from '../hooks/useFilterManager';
import { calculateSessionKpis } from '../utils/calculateSessionKpis';
import { enhanceSessionsResponse, isInDemoMode } from '../services/demoDataService';
import { useStytchMemberSession } from '@stytch/react/b2b';
import { getTraceSignalCounts } from '../utils/signalUtils';

const SIGNAL_PILL_VARIANTS = {
  danger: 'text-danger bg-danger/10',
  warning: 'text-warning bg-warning/10',
  info: 'text-accent-primary-base bg-accent-primary-base/10',
  success: 'text-green-400 bg-green-500/10',
  neutral: 'text-text-muted bg-background-tertiary',
  warm: 'text-accent-warm-base bg-accent-warm-base/10',
};

const renderSignalLabel = ({ label, count }) => (count > 1 ? `${label} (${count})` : label);

const VirtualSessionRow = React.memo(({ data, index, style }) => {
  const { data: sessions, columnWidths, onOpen: onNavigate, selectedSessionId } = data;
  const session = sessions[index];
  const isSelected = session.session_id === selectedSessionId;
  const [isHovered, setIsHovered] = React.useState(false);
  const signalCounts = React.useMemo(() => getTraceSignalCounts(session), [session]);
  const visibleSignalCounts = React.useMemo(() => signalCounts.slice(0, 3), [signalCounts]);
  const hiddenSignalsCount = signalCounts.length - visibleSignalCounts.length;
  const signalTitle = React.useMemo(() => {
    if (signalCounts.length === 0) return undefined;
    return signalCounts.map(renderSignalLabel).join(', ');
  }, [signalCounts]);

  const getStatusColor = () => {
    // Red dot for sessions with errors or security detections
    if (session.error_rate > 0 || session.has_prompt_injection || session.has_data_exfiltration) {
      return 'bg-danger';
    }
    // Green/primary dot for healthy sessions
    return 'bg-accent-primary-base';
  };

  return (
    <div
      style={{
        ...style,
        ...getTableRowStyle(index, {
          isHovered,
          isSelected,
          interactive: true,
        }),
      }}
      className={`${components.table.row.container} ${components.table.row.border} cursor-pointer`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onNavigate(session.session_id, session)}
    >
      {/* Entity: User ID */}
      <div
        className={`${components.table.cell.padding} ${components.table.cell.text.muted} truncate`}
        style={{ width: `${columnWidths.userId}px`, minWidth: `${columnWidths.userId}px`, flex: 'none', color: components.table.cell.colors.entity }}
      >
        <div className="flex flex-col" style={{ gap: 1 }}>
          <span className="text-sm truncate" style={{ color: components.table.cell.colors.entity }}>
            {session.user_id || '—'}
          </span>
          {session.workflow_name && (
            <span style={{ fontSize: 10, color: palette.text.muted, lineHeight: 1.2 }}>
              {session.workflow_name}
            </span>
          )}
        </div>
      </div>
      <div style={{ width: '3px', flex: 'none' }} />
      {/* Time: Last Event Time */}
      <div
        className={`${components.table.cell.padding} ${components.table.cell.text.muted}`}
        style={{ width: `${columnWidths.lastEvent}px`, minWidth: `${columnWidths.lastEvent}px`, flex: 'none', color: components.table.cell.colors.time }}
      >
        {formatRelativeTime(session.session_start)}
      </div>
      <div style={{ width: '3px', flex: 'none' }} />
      {/* Metric: Turns */}
      <div
        className={`${components.table.cell.padding} ${components.table.cell.text.metric}`}
        style={{ width: `${columnWidths.turns}px`, minWidth: `${columnWidths.turns}px`, flex: 'none', color: components.table.cell.colors.metric }}
      >
        {session.trace_count || 0}
      </div>
      <div style={{ width: '3px', flex: 'none' }} />
      {/* Signals */}
      <div
        className={`${components.table.cell.padding} ${components.table.cell.text.body} truncate`}
        style={{ width: `${columnWidths.quality}px`, minWidth: `${columnWidths.quality}px`, flex: 'none' }}
        title={signalTitle}
      >
        {signalCounts.length > 0 ? (
          <div className="flex items-center gap-1.5 overflow-hidden whitespace-nowrap">
            {visibleSignalCounts.map((signal, idx) => (
              <span
                key={`${signal.label}-${idx}`}
                className={`px-2 py-0.5 rounded-full text-[10px] font-medium border border-border-strong truncate ${SIGNAL_PILL_VARIANTS[signal.variant] || SIGNAL_PILL_VARIANTS.neutral}`}
              >
                {renderSignalLabel(signal)}
              </span>
            ))}
            {hiddenSignalsCount > 0 && (
              <span
                className={`px-2 py-0.5 rounded-full text-[10px] font-medium border border-border-strong ${SIGNAL_PILL_VARIANTS.neutral}`}
              >
                +{hiddenSignalsCount}
              </span>
            )}
          </div>
        ) : (
          <span className={`${components.table.cell.text.muted}`} style={{ color: components.table.cell.colors.metric }}>—</span>
        )}
      </div>
      <div style={{ width: '3px', flex: 'none' }} />
      {/* Metric: Latency */}
      <div
        className={`${components.table.cell.padding} ${components.table.cell.text.metric}`}
        style={{ width: `${columnWidths.latency}px`, minWidth: `${columnWidths.latency}px`, flex: 'none', color: components.table.cell.colors.metric }}
      >
        {formatDuration(session.total_duration_ms)}
      </div>
      <div style={{ width: '3px', flex: 'none' }} />
      {/* Metric: Tokens */}
      <div
        className={`${components.table.cell.padding} ${components.table.cell.text.metric}`}
        style={{ width: `${columnWidths.tokens}px`, minWidth: `${columnWidths.tokens}px`, flex: 'none', color: components.table.cell.colors.metric }}
      >
        {formatTokens(session.total_tokens || 0)}
      </div>
      <div style={{ width: '3px', flex: 'none' }} />
      {/* Metric: Cost */}
      <div
        className={`${components.table.cell.padding} ${components.table.cell.text.metric}`}
        style={{ width: `${columnWidths.cost}px`, minWidth: `${columnWidths.cost}px`, flex: 'none', color: components.table.cell.colors.metric }}
      >
        {formatCost(session.cost)}
      </div>
      <div style={{ width: '3px', flex: 'none' }} />
      {/* Entity: Session ID */}
      <div
        className={`flex items-center ${components.table.cell.gap} ${components.table.cell.padding}`}
        style={{ width: `${columnWidths.sessionId}px`, minWidth: `${columnWidths.sessionId}px`, flex: 'none' }}
      >
        <div className={`${iconSize.dot} ${iconShape.dot} flex-shrink-0 ${getStatusColor()}`}></div>
        <span className={`${components.table.cell.text.id} truncate`} style={{ color: components.table.cell.colors.entity }} title={session.session_id}>
          {session.session_id}
        </span>
      </div>
      <div style={{ width: '3px', flex: 'none' }} />
      {/* Metric: Spans */}
      <div
        className={`${components.table.cell.padding} ${components.table.cell.text.metric}`}
        style={{ width: `${columnWidths.spans}px`, minWidth: `${columnWidths.spans}px`, flex: 'none', color: components.table.cell.colors.metric }}
      >
        {(session.total_spans || 0).toLocaleString()}
      </div>
    </div>
  );
});

export default function Sessions({ searchResults = null, filterValues = {}, onEntitiesUpdate }) {
  const navigate = useNavigateWithRange();
  const [searchParams] = useSearchParams();
  const { selectedAppId } = useParams(); // Extract selectedAppId from /app/:selectedAppId/sessions route
  const { selectedRange } = useTimeRange();
  const { selectedEnvironment, environments } = useEnvironment();
  const { selectedAppName } = useApp();

  // Get selected session ID from URL (persists across navigation and page reloads)
  const selectedSessionId = searchParams.get('selectedSessionId');

  // Resolve app name for breadcrumb
  const appName = useMemo(() => {
    // Try AppContext first (fastest)
    if (selectedAppName) return selectedAppName;

    // Fallback: search in environments cache
    if (selectedAppId && environments.length) {
      for (const env of environments) {
        const app = env.apps?.find(a => a.id === selectedAppId);
        if (app) return app.name;
      }
    }

    // Last resort: use ID
    return selectedAppId;
  }, [selectedAppId, selectedAppName, environments]);

  // Breadcrumb: Home → App Name → Sessions
  const breadcrumbItems = useMemo(() => {
    const params = new URLSearchParams();
    if (selectedEnvironment) params.set('env', selectedEnvironment);
    if (selectedRange) params.set('range', selectedRange);
    const queryString = params.toString() ? `?${params.toString()}` : '';

    return [
      { label: appName, to: `/app/${selectedAppId}${queryString}` },
      { label: 'Sessions' },
    ];
  }, [appName, selectedAppId, selectedEnvironment, selectedRange]);

  const { session } = useStytchMemberSession();
  const tenantId = session?.organization_id || null;
  const isDemo = isInDemoMode(tenantId, selectedEnvironment, selectedAppId);

  // Fetch sessions — skip API call for demo apps (non-numeric app IDs crash the query)
  const { sessions: apiSessions, loading, error } = useSessionList(selectedAppId, !isDemo);

  const [kpis, setKpis] = useState(null);
  const sessionsData = isDemo
    ? (enhanceSessionsResponse(null, tenantId, selectedEnvironment, selectedAppId)?.sessions || null)
    : apiSessions;

  // Local filter manager (standalone mode only)
  const {
    userFilter: _localUserFilter,
    addEntityFilter,
    removeEntityFilter
  } = useFilterManager({});

  // In embedded mode TelemetryTab owns the filter state; use parent's value
  const isEmbedded = filterValues && Object.keys(filterValues).length > 0;
  const userFilter = isEmbedded ? (filterValues.userFilter || '') : (_localUserFilter || '');

  // Extract unique values for filters
  const userIdOptions = useMemo(() => {
    if (!sessionsData) return [];
    const userIds = sessionsData
      .map(s => s.user_id)
      .filter(Boolean);
    return [...new Set(userIds)];
  }, [sessionsData]);

  // Bubble user IDs up to TelemetryTab for FilterPills options
  useEffect(() => {
    if (onEntitiesUpdate && userIdOptions.length > 0) {
      onEntitiesUpdate({ users: userIdOptions });
    }
  }, [userIdOptions, onEntitiesUpdate]);

  // Apply filters
  const filteredSessions = useMemo(() => {
    if (!sessionsData) return [];
    let sessions = sessionsData;

    if (userFilter) {
      sessions = sessions.filter(s => s.user_id === userFilter);
    }

    return sessions;
  }, [sessionsData, userFilter]);

  // When searchResults are provided, group traces by session_id to build synthetic sessions
  const displaySessions = useMemo(() => {
    if (!searchResults) return filteredSessions;

    const grouped = {};
    searchResults.forEach(trace => {
      if (!trace.session_id) return;
      if (userFilter && trace.user_id !== userFilter) return;
      if (!grouped[trace.session_id]) {
        grouped[trace.session_id] = {
          session_id: trace.session_id,
          user_id: trace.user_id || null,
          session_start: trace.trace_start,
          trace_count: 0,
          total_tokens: 0,
          cost: 0,
          error_spans: 0,
          total_spans: 0,
          total_duration_ms: null,
          matchedTraces: [],
          workflow_name: null,
        };
      }
      const s = grouped[trace.session_id];
      s.trace_count++;
      s.total_tokens += (trace.tokens || 0);
      s.cost += (trace.cost || 0);
      s.matchedTraces.push(trace);
      if (!s.workflow_name && trace.workflow?.workflow_name) s.workflow_name = trace.workflow.workflow_name;
    });

    return Object.values(grouped).sort((a, b) =>
      new Date(b.session_start) - new Date(a.session_start)
    );
  }, [searchResults, filteredSessions, userFilter]);

  const handleSessionNavigate = useCallback((sessionId, sessionSummary = null) => {
    if (!sessionId) throw new Error('sessionId is required for navigation');

    navigate(`/app/${selectedAppId}/sessions/${sessionId}`, {
      state: sessionSummary ? { sessionSummary } : undefined
    });
  }, [navigate, selectedAppId]);

  // Memoize itemData for virtualization
  const itemData = useMemo(
    () => ({ sessions: displaySessions, onNavigate: handleSessionNavigate, selectedSessionId }),
    [displaySessions, handleSessionNavigate, selectedSessionId]
  );

  // Calculate KPIs when data changes
  useEffect(() => {
    if (sessionsData) {
      const calculatedKpis = calculateSessionKpis(sessionsData, 0);
      setKpis(calculatedKpis);
    }
  }, [sessionsData]);

  // Filter management functions
  const getActiveFilters = () => {
    const filters = [];
    if (userFilter) filters.push({ key: 'user', label: 'User ID', value: userFilter });
    return filters;
  };

  const getAvailableFilters = () => [
    { key: 'user', label: 'User ID', options: userIdOptions }
  ];

  if (error && !isDemo) {
    return <ErrorState title="Error Loading Sessions" message={error} />;
  }

  const statsContent = kpis && (
    <StatsBar>
      <Stat label="Active Sessions" value={kpis.activeSessions.toLocaleString()} />
      <Stat label="LLM Spend" value={`$${kpis.totalCost.toFixed(2)}`} />
      <Stat label="Tokens" value={formatTokens(kpis.totalTokens)} />
      <Stat label="Median Turns" value={kpis.medianTurns} />
    </StatsBar>
  );

  const filtersContent = (
    <FilterPills
      activeFilters={getActiveFilters()}
      availableFilters={getAvailableFilters()}
      onAddFilter={addEntityFilter}
      onRemoveFilter={removeEntityFilter}
    />
  );

  const tableContent = (
    <ResizableTable
      columns={[
        { key: 'userId', label: 'User ID', defaultWidthPercent: 13, minWidth: 130 },
        { key: 'lastEvent', label: 'Start Time', defaultWidthPercent: 9, minWidth: 110 },
        { key: 'turns', label: 'Turns', defaultWidthPercent: 7, minWidth: 80 },
        { key: 'quality', label: 'Signals', defaultWidthPercent: 16.2, minWidth: 240 },
        { key: 'latency', label: 'Duration', defaultWidthPercent: 8, minWidth: 100 },
        { key: 'tokens', label: 'Tokens', defaultWidthPercent: 7, minWidth: 100 },
        { key: 'cost', label: 'Cost', defaultWidthPercent: 4, minWidth: 90 },
        { key: 'sessionId', label: 'Session ID', defaultWidthPercent: 12, minWidth: 200 },
        { key: 'spans', label: 'Spans', defaultWidthPercent: 5, minWidth: 70 },
      ]}
      data={displaySessions}
      renderRow={VirtualSessionRow}
      onOpen={handleSessionNavigate}
      selectedSessionId={selectedSessionId}
      storageKey="lumoz-sessions-table-widths"
      rowHeight={layout.rowHeight}
      containerHeight={filterValues && Object.keys(filterValues).length > 0 ? 'calc(100vh - 300px)' : components.table.listContainer}
    />
  );

  // Embedded in TelemetryTab — pure display table
  if (filterValues && Object.keys(filterValues).length > 0) {
    return (
      <div className="flex flex-col h-full gap-3">
        {/* Filter bar is owned by TelemetryTab in embedded mode; only the
            search-result count is shown here. */}
        {!loading && searchResults !== null && (
          <div className="flex-shrink-0 flex items-center gap-2">
            <span style={{ fontSize: 12, color: palette.text.muted }}>
              {displaySessions.length} session{displaySessions.length !== 1 ? 's' : ''} with matching traces
            </span>
          </div>
        )}
        <div className="flex-1 min-h-0">
          {loading && searchResults === null ? (
            <div className={components.table.empty}>Loading sessions...</div>
          ) : displaySessions.length === 0 ? (
            <div className={components.table.empty}>
              {searchResults !== null ? 'No sessions found for search results' : 'No sessions found'}
            </div>
          ) : (
            tableContent
          )}
        </div>
        {/* Footer: stats bar (non-search mode) */}
        {!loading && !searchResults && statsContent && (
          <div className="flex-shrink-0 pt-4 px-2">{statsContent}</div>
        )}
      </div>
    );
  }

  return (
    <ListPageLayout
      breadcrumb={<Breadcrumb items={breadcrumbItems} />}
      title="Sessions"
      stats={statsContent}
      filters={filtersContent}
      loading={loading}
      error={error}
      info={null}
      data={filteredSessions}
    >
      {tableContent}
    </ListPageLayout>
  );
}
